import { useRef } from 'react';
import { useMutation, type UseMutationOptions, type UseMutationResult } from '@tanstack/react-query';
import { actionCatalog, failureTitle, type ActionFeedback, type ActionId } from './actionCatalog';
import { confirmAction, feedbackStore, isUnconfirmed, notify, safeActionError, UnconfirmedActionError } from './notifications';
import { assertReadConfirmed } from './readFeedback';

class CancelledAction extends Error {}
export const SLOW_ACTION_MS = 20_000;
const inFlight = new Map<string, Promise<unknown>>();
/** User-triggered mutations only. Background queries/realtime do not produce toasts. */
export function useActionMutation<TData = unknown, TError = Error, TVariables = void, TContext = unknown>(
  actionId: ActionId,
  options: UseMutationOptions<TData, TError, TVariables, TContext>,
): UseMutationResult<TData, TError, TVariables, TContext> {
  const feedback: ActionFeedback = actionCatalog[actionId];
  const active = useRef<Promise<TData> | null>(null);
  const initiator = useRef<HTMLElement | null>(null);
  const notice = useRef<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const requestEpoch = useRef(0);
  const accepted = useRef(false);
  const cancelled = useRef(false);
  const current = () => requestEpoch.current === feedbackStore.getEpoch();
  const mutation = useMutation<TData, TError, TVariables, TContext>({
    ...options,
    retry: false,
    mutationFn: async (variables, context) => {
      requestEpoch.current = feedbackStore.getEpoch(); accepted.current = false; cancelled.current = false;
      if (feedback.confirmation && !await confirmAction(feedback.confirmation, initiator.current)) { cancelled.current = true; throw new CancelledAction(); }
      if (!current()) { cancelled.current = true; throw new CancelledAction(); }
      notice.current = notify.loading(`${feedback.label}…`);
      timer.current = setTimeout(() => {
        if (current()) notify.warning('Status not confirmed', { id: notice.current!, description: `${feedback.label} is taking longer than expected. The request is still pending. Check the authoritative record before retrying; do not submit another payment or duplicate action.` });
      }, SLOW_ACTION_MS);
      if (!options.mutationFn || typeof options.mutationFn !== 'function') throw new Error('Missing action implementation');
      const data = await options.mutationFn(variables, context);
      accepted.current = true;
      return data;
    },
    onSuccess: async (data, variables, result, context) => {
      if (!current() && !actionId.startsWith('auth.')) return;
      const refreshed = await options.onSuccess?.(data, variables, result, context);
      assertReadConfirmed(refreshed);
      if (current() || actionId.startsWith('auth.')) {
        const message = typeof feedback.success === 'function' ? feedback.success(data, variables) : { title: feedback.success };
        notify.success(message.title, { id: notice.current!, ...(message.description ? { description: message.description } : {}) });
      }
    },
    onError: async (error, variables, result, context) => {
      if (cancelled.current) return;
      if (current()) {
        const uncertain = accepted.current || isUnconfirmed(error);
        const title = uncertain ? 'Status not confirmed' : failureTitle(feedback.label);
        const description = accepted.current ? 'The server accepted the action, but the refreshed view could not be confirmed. Refresh before making another change.' : safeActionError(error);
        if (uncertain) notify.warning(title, { ...(notice.current ? { id: notice.current } : {}), description });
        else notify.error(title, { ...(notice.current ? { id: notice.current } : {}), description });
      }
      if (current()) await options.onError?.(error, variables, result, context);
    },
    onSettled: async (data, error, variables, result, context) => {
      clearTimeout(timer.current);
      // Existing caller cleanup must run even if the confirmation is cancelled.
      await options.onSettled?.(data, error, variables, result, context);
    },
  });
  const mutateAsync: typeof mutation.mutateAsync = (...args) => {
    if (active.current) return active.current;
    const key = `${feedbackStore.getEpoch()}:${actionId}`;
    if (inFlight.has(key)) {
      notify.warning('An action is already pending', { id: `pending-${actionId}`, description: 'Wait for the current request and refresh its result before submitting another action of this kind.' });
      return Promise.reject(new UnconfirmedActionError());
    }
    initiator.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    requestEpoch.current = feedbackStore.getEpoch(); accepted.current = false; cancelled.current = false; notice.current = null;
    const promise = mutation.mutateAsync(...args).finally(() => { active.current = null; inFlight.delete(key); notify.dismiss(`pending-${actionId}`); });
    active.current = promise;
    inFlight.set(key, promise);
    return promise;
  };
  const error = mutation.error && (accepted.current ? new UnconfirmedActionError() : new Error(safeActionError(mutation.error)));
  return { ...mutation, error: cancelled.current ? null : error, isError: cancelled.current ? false : mutation.isError,
    mutateAsync, mutate: (...args: Parameters<typeof mutation.mutate>) => { void mutateAsync(...args).catch(() => undefined); },
  } as UseMutationResult<TData, TError, TVariables, TContext>;
}
